import React, { useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Ticket, Operator, BackendTicketStatus } from '../types';

// ── Shared SegmentedControl ────────────────────────────────────────────
interface SegOpt { id: string; label: string }
const SegmentedControl = ({ options, value, onChange }: {
  options: SegOpt[]; value: string; onChange: (id: string) => void;
}) => (
  <div className="bg-[#EFEFEF] rounded-[13px] p-[3px] grid grid-cols-2 w-full gap-[5px]">
    {options.map((opt) => (
      <button
        key={opt.id}
        onClick={() => onChange(opt.id)}
        className={`py-[9px] px-1.5 rounded-[10px] text-[14px] transition-all duration-200 text-center leading-snug min-h-[44px] ${
          value === opt.id
            ? 'bg-white shadow-[0_2px_8px_rgba(0,0,0,0.13),0_1px_2px_rgba(0,0,0,0.08)] text-black font-semibold'
            : 'text-[#8E8E93] font-normal hover:text-black/60'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  operators: Operator[];
  onStatusChange: (status: BackendTicketStatus) => void;
  onPriorityChange: (priority: 'high' | 'medium' | 'low') => void;
  onAssign: (operatorId: string | null) => void;
  onDeleteTicket: () => void;
}

export const ActionModal: React.FC<ActionModalProps> = ({
  isOpen, onClose, ticket, operators, onStatusChange, onPriorityChange, onAssign, onDeleteTicket,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const statusOpts: SegOpt[] = [
    { id: 'NEW', label: 'Новый' },
    { id: 'OPEN', label: 'Открыт' },
    { id: 'IN_PROGRESS', label: 'В работе' },
    { id: 'WAITING_USER', label: 'Ждём клиента' },
    { id: 'WAITING_OPERATOR', label: 'Ждёт оператора' },
    { id: 'CLOSED', label: 'Закрыт' },
    { id: 'CANCELED', label: 'Отменён' },
    { id: 'SPAM', label: 'Спам' },
  ];
  const priorityOpts: SegOpt[] = [
    { id: 'high', label: 'Высокий' },
    { id: 'medium', label: 'Средний' },
    { id: 'low', label: 'Низкий' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black/32 backdrop-blur-[2px] z-[100]"
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 32 }}
            transition={{ type: 'spring', damping: 30, stiffness: 400, mass: 0.7 }}
            className="fixed z-[101] bottom-0 left-0 right-0 md:inset-0 md:flex md:items-center md:justify-center pointer-events-none"
          >
            <div
              className="bg-white rounded-t-[24px] md:rounded-[24px] w-full md:w-[400px] shadow-[0_-4px_40px_rgba(0,0,0,0.18),0_20px_60px_rgba(0,0,0,0.2)] pointer-events-auto overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="md:hidden flex justify-center pt-3 pb-2">
                <div className="w-10 h-[4px] rounded-full bg-[#D1D1D6]" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#F2F2F7]">
                <div>
                  <p className="text-[17px] font-semibold text-black tracking-[-0.2px]">Управление тикетом</p>
                  <p className="text-[12px] text-[#8E8E93] mt-0.5">#{ticket.id.slice(-6)}</p>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full bg-[#F2F2F7] flex items-center justify-center text-[#8E8E93] hover:bg-[#E5E5EA] transition-colors"
                >
                  <X size={15} strokeWidth={2.5} />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="px-5 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                {/* Status */}
                <div>
                    <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-2.5">Статус</p>
                  <SegmentedControl
                    options={statusOpts}
                    value={ticket.backendStatus || 'OPEN'}
                    onChange={(v) => { onStatusChange(v as BackendTicketStatus); }}
                  />
                </div>

                {/* Priority */}
                <div>
                  <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-2.5">Приоритет</p>
                  <SegmentedControl
                    options={priorityOpts}
                    value={ticket.priority}
                    onChange={(v) => { onPriorityChange(v as any); }}
                  />
                </div>

                {/* Operators */}
                <div>
                  <p className="text-[12px] font-semibold text-[#8E8E93] uppercase tracking-widest mb-2.5">Назначить оператора</p>
                  <div className="space-y-2">
                    {operators.map(op => {
                      const isAssigned = ticket.assignedTo === op.id;
                      return (
                        <button
                          key={op.id}
                          onClick={() => onAssign(isAssigned ? null : op.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-[14px] transition-all duration-200 ${
                            isAssigned
                              ? 'bg-[#007AFF] shadow-[0_2px_12px_rgba(0,122,255,0.3)]'
                              : 'bg-[#F5F5F7] hover:bg-[#EBEBF0] active:scale-[0.99]'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[15px] font-bold shrink-0 ${
                            isAssigned ? 'bg-white/25 text-white' : 'bg-[#007AFF]/12 text-[#007AFF]'
                          }`}>
                            {op.name.charAt(0)}
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`text-[15px] font-medium leading-tight ${isAssigned ? 'text-white' : 'text-black'}`}>
                              {op.name}
                            </p>
                            <p className={`text-[12px] flex items-center gap-1.5 mt-0.5 ${isAssigned ? 'text-white/65' : 'text-[#8E8E93]'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${op.isOnline ? 'bg-[#34C759]' : 'bg-[#C7C7CC]'} ${isAssigned ? 'bg-white/50' : ''}`} />
                              {op.isOnline ? 'Онлайн' : 'Офлайн'}
                            </p>
                          </div>
                          {isAssigned && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-[12px] text-white/80 font-medium">Назначен</span>
                              <Check size={16} className="text-white" strokeWidth={2.5} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 pb-6 md:pb-5 pt-2">
                <button
                  onClick={onDeleteTicket}
                  className="w-full mb-2 bg-[#FF3B30]/14 hover:bg-[#FF3B30]/22 active:scale-[0.98] text-[#C93028] text-[15px] font-semibold py-[12px] rounded-[14px] transition-all duration-150"
                >
                  Удалить тикет и историю
                </button>
                <button
                  onClick={onClose}
                  className="w-full bg-[#007AFF] hover:bg-[#0066DD] active:scale-[0.98] text-white text-[16px] font-semibold py-[14px] rounded-[14px] transition-all duration-150"
                >
                  Готово
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
